import React, {
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    FiAlertCircle,
    FiCheckCircle,
    FiCreditCard,
    FiLoader,
    FiSearch,
    FiX,
} from "react-icons/fi";

import {
    useDispatch,
    useSelector,
} from "react-redux";

import {
    clearPaymentOutOutstandingBills,
    createPaymentOut,
    fetchSupplierOutstandingPurchaseBills,
    paymentOutErrorSelector,
    paymentOutLoadingSelector,
    paymentOutOutstandingBillsSelector,
    paymentOutOutstandingSummarySelector,
} from "../../../../../../../store/billing/paymentOut/paymentOutSlice";

import "../../SupplierLedger/SupplierLedger.css";
import "../PaymentOut.css";


/*
|--------------------------------------------------------------------------
| CONSTANTS
|--------------------------------------------------------------------------
*/

const PAYMENT_METHODS = [
    {
        value:
            "CASH",

        label:
            "Cash",
    },
    {
        value:
            "UPI",

        label:
            "UPI",
    },
    {
        value:
            "BANK_TRANSFER",

        label:
            "Bank Transfer",
    },
    {
        value:
            "CHEQUE",

        label:
            "Cheque",
    },
    {
        value:
            "CARD",

        label:
            "Card",
    },
    {
        value:
            "OTHER",

        label:
            "Other",
    },
];


/*
|--------------------------------------------------------------------------
| HELPERS
|--------------------------------------------------------------------------
*/

const toNumber = (
    value,
    fallback = 0
) => {
    const number =
        Number(
            value
        );

    return Number.isFinite(
        number
    )
        ? number
        : fallback;
};


const roundMoney = (
    value
) =>
    Math.round(
        (
            toNumber(
                value
            ) +
            Number.EPSILON
        ) *
        100
    ) / 100;


const formatCurrency = (
    value
) =>
    new Intl.NumberFormat(
        "en-IN",
        {
            style:
                "currency",

            currency:
                "INR",

            minimumFractionDigits:
                2,

            maximumFractionDigits:
                2,
        }
    ).format(
        roundMoney(
            value
        )
    );


const formatDate = (
    value
) => {
    if (
        !value
    ) {
        return "-";
    }

    const date =
        new Date(
            value
        );

    if (
        Number.isNaN(
            date.getTime()
        )
    ) {
        return "-";
    }

    return new Intl.DateTimeFormat(
        "en-IN",
        {
            day:
                "2-digit",

            month:
                "short",

            year:
                "numeric",
        }
    ).format(
        date
    );
};


const getSupplierId = (
    supplier
) =>
    supplier?._id ||
    supplier?.id ||
    "";


const getSupplierName = (
    supplier
) =>
    supplier
        ?.supplierName ||
    supplier
        ?.vendorName ||
    supplier
        ?.companyName ||
    supplier
        ?.name ||
    "Supplier";


const getSupplierMobile = (
    supplier
) =>
    supplier?.mobile ||
    "-";


const getSupplierGstin = (
    supplier
) =>
    supplier?.gstin ||
    supplier?.gstNumber ||
    "-";


const getBillId = (
    bill
) =>
    bill?._id ||
    bill?.id ||
    "";


const getBillNumber = (
    bill
) =>
    bill
        ?.documentNumber ||
    bill
        ?.purchaseBillNumber ||
    bill
        ?.billNumber ||
    "-";


const getBillDate = (
    bill
) =>
    bill
        ?.documentDate ||
    bill
        ?.purchaseBillDate ||
    bill
        ?.billDate ||
    bill
        ?.createdAt ||
    null;


const getBillTotal = (
    bill
) =>
    roundMoney(
        bill
            ?.totals
            ?.grandTotal ??
        bill
            ?.grandTotal ??
        bill
            ?.totalAmount ??
        0
    );


const getBillBalance = (
    bill
) =>
    roundMoney(
        bill
            ?.balanceAmount ??
        bill
            ?.totals
            ?.balanceAmount ??
        bill
            ?.dueAmount ??
        bill
            ?.outstandingAmount ??
        0
    );


const getTodayInputValue =
    () => {
        const date =
            new Date();

        const year =
            date.getFullYear();

        const month =
            String(
                date.getMonth() +
                1
            ).padStart(
                2,
                "0"
            );

        const day =
            String(
                date.getDate()
            ).padStart(
                2,
                "0"
            );

        return `${year}-${month}-${day}`;
    };


const getErrorMessage = (
    error,
    fallback
) => {
    if (
        typeof error ===
        "string"
    ) {
        return error;
    }

    return (
        error
            ?.response
            ?.data
            ?.message ||
        error
            ?.response
            ?.data
            ?.error ||
        error
            ?.message ||
        fallback
    );
};


/*
|--------------------------------------------------------------------------
| COMPONENT
|--------------------------------------------------------------------------
*/

const MakePaymentDialog = ({
    open = false,

    onClose,

    onSuccess,

    suppliers = [],

    supplierLoading = false,

    supplierError = "",

    preselectedSupplier =
    null,
}) => {
    const dispatch =
        useDispatch();


    /*
    |--------------------------------------------------------------------------
    | REDUX
    |--------------------------------------------------------------------------
    */

    const outstandingBills =
        useSelector(
            paymentOutOutstandingBillsSelector
        ) || [];

    const outstandingSummary =
        useSelector(
            paymentOutOutstandingSummarySelector
        ) || {};

    const loading =
        useSelector(
            paymentOutLoadingSelector
        ) || {};

    const errors =
        useSelector(
            paymentOutErrorSelector
        ) || {};


    /*
    |--------------------------------------------------------------------------
    | FORM STATE
    |--------------------------------------------------------------------------
    */

    const [
        supplierId,
        setSupplierId,
    ] =
        useState(
            ""
        );

    const [
        paymentDate,
        setPaymentDate,
    ] =
        useState(
            getTodayInputValue()
        );

    const [
        amount,
        setAmount,
    ] =
        useState(
            ""
        );

    const [
        paymentMethod,
        setPaymentMethod,
    ] =
        useState(
            "CASH"
        );

    const [
        referenceNumber,
        setReferenceNumber,
    ] =
        useState(
            ""
        );

    const [
        bankName,
        setBankName,
    ] =
        useState(
            ""
        );

    const [
        chequeNumber,
        setChequeNumber,
    ] =
        useState(
            ""
        );

    const [
        chequeDate,
        setChequeDate,
    ] =
        useState(
            ""
        );

    const [
        narration,
        setNarration,
    ] =
        useState(
            ""
        );

    const [
        billSearch,
        setBillSearch,
    ] =
        useState(
            ""
        );

    /*
    |--------------------------------------------------------------------------
    | {
    |    purchaseBillId: amount
    | }
    |--------------------------------------------------------------------------
    */

    const [
        allocations,
        setAllocations,
    ] =
        useState(
            {}
        );

    const [
        localError,
        setLocalError,
    ] =
        useState(
            ""
        );


    /*
    |--------------------------------------------------------------------------
    | NORMALIZED SUPPLIERS
    |--------------------------------------------------------------------------
    */

    const normalizedSuppliers =
        useMemo(
            () => {
                const records =
                    Array.isArray(
                        suppliers
                    )
                        ? suppliers
                        : [];

                /*
                |--------------------------------------------------------------------------
                | If preselected supplier is not already in dropdown,
                | include it at the beginning.
                |--------------------------------------------------------------------------
                */

                if (
                    preselectedSupplier &&
                    !records.some(
                        (
                            supplier
                        ) =>
                            String(
                                getSupplierId(
                                    supplier
                                )
                            ) ===
                            String(
                                getSupplierId(
                                    preselectedSupplier
                                )
                            )
                    )
                ) {
                    return [
                        preselectedSupplier,
                        ...records,
                    ];
                }

                return records;
            },
            [
                suppliers,
                preselectedSupplier,
            ]
        );


    /*
    |--------------------------------------------------------------------------
    | SELECTED SUPPLIER
    |--------------------------------------------------------------------------
    */

    const selectedSupplier =
        useMemo(
            () => {
                if (
                    !supplierId
                ) {
                    return null;
                }

                const foundSupplier =
                    normalizedSuppliers.find(
                        (
                            supplier
                        ) =>
                            String(
                                getSupplierId(
                                    supplier
                                )
                            ) ===
                            String(
                                supplierId
                            )
                    );

                if (
                    foundSupplier
                ) {
                    return foundSupplier;
                }

                if (
                    preselectedSupplier &&
                    String(
                        getSupplierId(
                            preselectedSupplier
                        )
                    ) ===
                    String(
                        supplierId
                    )
                ) {
                    return preselectedSupplier;
                }

                return null;
            },
            [
                normalizedSuppliers,
                supplierId,
                preselectedSupplier,
            ]
        );


    /*
    |--------------------------------------------------------------------------
    | RESET WHEN DIALOG OPENS
    |--------------------------------------------------------------------------
    */

    useEffect(
        () => {
            if (
                !open
            ) {
                return;
            }

            const initialSupplierId =
                getSupplierId(
                    preselectedSupplier
                );

            setSupplierId(
                initialSupplierId
            );

            setPaymentDate(
                getTodayInputValue()
            );

            setAmount(
                ""
            );

            setPaymentMethod(
                "CASH"
            );

            setReferenceNumber(
                ""
            );

            setBankName(
                ""
            );

            setChequeNumber(
                ""
            );

            setChequeDate(
                ""
            );

            setNarration(
                ""
            );

            setBillSearch(
                ""
            );

            setAllocations(
                {}
            );

            setLocalError(
                ""
            );

            dispatch(
                clearPaymentOutOutstandingBills()
            );
        },
        [
            open,
            preselectedSupplier,
            dispatch,
        ]
    );


    /*
    |--------------------------------------------------------------------------
    | LOAD OUTSTANDING PURCHASE BILLS
    |--------------------------------------------------------------------------
    */

    useEffect(
        () => {
            if (
                !open ||
                !supplierId
            ) {
                return undefined;
            }

            const timer =
                setTimeout(
                    () => {
                        dispatch(
                            fetchSupplierOutstandingPurchaseBills({
                                supplierId,

                                search:
                                    billSearch.trim(),

                                limit:
                                    100,
                            })
                        );
                    },
                    300
                );

            return () =>
                clearTimeout(
                    timer
                );
        },
        [
            dispatch,
            open,
            supplierId,
            billSearch,
        ]
    );


    /*
    |--------------------------------------------------------------------------
    | SUPPLIER CHANGE
    |--------------------------------------------------------------------------
    */

    const handleSupplierChange =
        (
            value
        ) => {
            setSupplierId(
                value
            );

            setAllocations(
                {}
            );

            setAmount(
                ""
            );

            setBillSearch(
                ""
            );

            setLocalError(
                ""
            );

            dispatch(
                clearPaymentOutOutstandingBills()
            );
        };


    /*
    |--------------------------------------------------------------------------
    | ALLOCATION CHANGE
    |--------------------------------------------------------------------------
    */

    const handleAllocationChange =
        (
            bill,
            value
        ) => {
            const billId =
                getBillId(
                    bill
                );

            if (
                !billId
            ) {
                return;
            }

            const balance =
                getBillBalance(
                    bill
                );

            let allocationAmount =
                roundMoney(
                    value
                );

            allocationAmount =
                Math.max(
                    allocationAmount,
                    0
                );

            allocationAmount =
                Math.min(
                    allocationAmount,
                    balance
                );

            setAllocations(
                (
                    current
                ) => ({
                    ...current,

                    [billId]:
                        allocationAmount,
                })
            );

            setLocalError(
                ""
            );
        };


    /*
    |--------------------------------------------------------------------------
    | PAY FULL BILL
    |--------------------------------------------------------------------------
    */

    const handlePayFullBill =
        (
            bill
        ) => {
            const billId =
                getBillId(
                    bill
                );

            if (
                !billId
            ) {
                return;
            }

            setAllocations(
                (
                    current
                ) => ({
                    ...current,

                    [billId]:
                        getBillBalance(
                            bill
                        ),
                })
            );

            setLocalError(
                ""
            );
        };


    /*
    |--------------------------------------------------------------------------
    | ALLOCATE ALL
    |--------------------------------------------------------------------------
    */

    const handleAllocateAll =
        () => {
            const next =
                {};

            outstandingBills.forEach(
                (
                    bill
                ) => {
                    const billId =
                        getBillId(
                            bill
                        );

                    if (
                        !billId
                    ) {
                        return;
                    }

                    const balance =
                        getBillBalance(
                            bill
                        );

                    if (
                        balance >
                        0
                    ) {
                        next[
                            billId
                        ] =
                            balance;
                    }
                }
            );

            setAllocations(
                next
            );

            const total =
                Object.values(
                    next
                ).reduce(
                    (
                        result,
                        value
                    ) =>
                        result +
                        toNumber(
                            value
                        ),
                    0
                );

            setAmount(
                String(
                    roundMoney(
                        total
                    )
                )
            );

            setLocalError(
                ""
            );
        };


    /*
    |--------------------------------------------------------------------------
    | CLEAR ALLOCATIONS
    |--------------------------------------------------------------------------
    */

    const handleClearAllocation =
        () => {
            setAllocations(
                {}
            );

            setLocalError(
                ""
            );
        };


    /*
    |--------------------------------------------------------------------------
    | ALLOCATION ROWS
    |--------------------------------------------------------------------------
    */

    const allocationRows =
        useMemo(
            () =>
                outstandingBills
                    .map(
                        (
                            bill
                        ) => {
                            const billId =
                                getBillId(
                                    bill
                                );

                            const allocatedAmount =
                                roundMoney(
                                    allocations[
                                    billId
                                    ] ||
                                    0
                                );

                            if (
                                allocatedAmount <=
                                0
                            ) {
                                return null;
                            }

                            return {
                                purchaseBillId:
                                    billId,

                                allocatedAmount,
                            };
                        }
                    )
                    .filter(
                        Boolean
                    ),
            [
                outstandingBills,
                allocations,
            ]
        );


    /*
    |--------------------------------------------------------------------------
    | TOTAL ALLOCATED
    |--------------------------------------------------------------------------
    */

    const totalAllocated =
        useMemo(
            () =>
                roundMoney(
                    allocationRows.reduce(
                        (
                            result,
                            row
                        ) =>
                            result +
                            toNumber(
                                row
                                    .allocatedAmount
                            ),
                        0
                    )
                ),
            [
                allocationRows,
            ]
        );


    /*
    |--------------------------------------------------------------------------
    | PAYMENT AMOUNT
    |--------------------------------------------------------------------------
    */

    const paymentAmount =
        roundMoney(
            amount
        );


    /*
    |--------------------------------------------------------------------------
    | SUPPLIER ADVANCE
    |--------------------------------------------------------------------------
    */

    const unallocatedAmount =
        roundMoney(
            Math.max(
                paymentAmount -
                totalAllocated,
                0
            )
        );


    /*
    |--------------------------------------------------------------------------
    | ALLOCATION SHORTAGE
    |--------------------------------------------------------------------------
    */

    const amountShortage =
        roundMoney(
            Math.max(
                totalAllocated -
                paymentAmount,
                0
            )
        );


    /*
    |--------------------------------------------------------------------------
    | AUTO INCREASE PAYMENT AMOUNT
    |--------------------------------------------------------------------------
    |
    | Example:
    |
    | Payment amount = 500
    | allocations = 1,000
    |
    | Automatically increase payment amount to 1,000.
    |--------------------------------------------------------------------------
    */

    useEffect(
        () => {
            if (
                totalAllocated <=
                0
            ) {
                return;
            }

            if (
                paymentAmount <
                totalAllocated
            ) {
                setAmount(
                    String(
                        totalAllocated
                    )
                );
            }
        },
        [
            totalAllocated,
            paymentAmount,
        ]
    );


    /*
    |--------------------------------------------------------------------------
    | VALIDATION
    |--------------------------------------------------------------------------
    */

    const validate =
        () => {
            if (
                !supplierId
            ) {
                return "Select a supplier.";
            }

            if (
                paymentAmount <=
                0
            ) {
                return "Payment amount must be greater than zero.";
            }

            if (
                totalAllocated >
                paymentAmount
            ) {
                return "Allocated amount cannot exceed payment amount.";
            }

            if (
                paymentMethod ===
                "CHEQUE" &&
                !chequeNumber.trim()
            ) {
                return "Cheque number is required.";
            }

            if (
                paymentMethod ===
                "CHEQUE" &&
                !chequeDate
            ) {
                return "Cheque date is required.";
            }

            return "";
        };


    /*
    |--------------------------------------------------------------------------
    | SUBMIT
    |--------------------------------------------------------------------------
    */

    const handleSubmit =
        async () => {
            const validationError =
                validate();

            if (
                validationError
            ) {
                setLocalError(
                    validationError
                );

                return;
            }

            try {
                setLocalError(
                    ""
                );

                const payload = {
                    supplierId,

                    paymentDate,

                    amount:
                        paymentAmount,

                    paymentMethod,

                    referenceNumber:
                        referenceNumber.trim(),

                    bankName:
                        bankName.trim(),

                    chequeNumber:
                        chequeNumber.trim(),

                    chequeDate:
                        chequeDate ||
                        null,

                    narration:
                        narration.trim(),

                    allocations:
                        allocationRows,
                };

                const payment =
                    await dispatch(
                        createPaymentOut(
                            payload
                        )
                    ).unwrap();

                await onSuccess?.(
                    payment
                );
            } catch (
            error
            ) {
                setLocalError(
                    getErrorMessage(
                        error,
                        "Unable to record supplier payment."
                    )
                );
            }
        };


    /*
    |--------------------------------------------------------------------------
    | CLOSE
    |--------------------------------------------------------------------------
    */

    const handleClose =
        () => {
            if (
                loading.create
            ) {
                return;
            }

            dispatch(
                clearPaymentOutOutstandingBills()
            );

            setLocalError(
                ""
            );

            onClose?.();
        };


    /*
    |--------------------------------------------------------------------------
    | CLOSED
    |--------------------------------------------------------------------------
    */

    if (
        !open
    ) {
        return null;
    }


    /*
    |--------------------------------------------------------------------------
    | RENDER
    |--------------------------------------------------------------------------
    */

    return (
        <div
            className="payment-out-dialog-overlay"
            onMouseDown={(event) => {
                if (
                    event.target ===
                    event.currentTarget
                ) {
                    handleClose();
                }
            }}
        >
            <div
                className="payment-out-dialog"
                role="dialog"
                aria-modal="true"
                aria-labelledby="make-payment-dialog-title"
                onMouseDown={(event) =>
                    event.stopPropagation()
                }
            >
                {/* =====================================================
                    HEADER
                ===================================================== */}

                <header className="payment-out-dialog-header">
                    <div>
                        <small>
                            SUPPLIER PAYMENT
                        </small>

                        <h2 id="make-payment-dialog-title">
                            Make Payment
                        </h2>

                        <p>
                            Record a payment and allocate it against outstanding purchase bills.
                        </p>
                    </div>

                    <button
                        type="button"
                        disabled={
                            loading.create
                        }
                        onClick={
                            handleClose
                        }
                        aria-label="Close"
                    >
                        <FiX />
                    </button>
                </header>


                {/* =====================================================
                    BODY
                ===================================================== */}

                <div className="payment-out-dialog-body">

                    {/* =================================================
                        ERRORS
                    ================================================= */}

                    {(localError ||
                        supplierError ||
                        errors.create ||
                        errors.outstandingBills) && (
                            <div className="payment-out-alert">
                                <FiAlertCircle />

                                <span>
                                    {localError ||
                                        supplierError ||
                                        errors.create ||
                                        errors.outstandingBills}
                                </span>
                            </div>
                        )}


                    {/* =================================================
                        PAYMENT FORM
                    ================================================= */}

                    <section className="payment-out-form-card">
                        <div className="payment-out-form-grid">

                            {/* =============================================
                                SUPPLIER
                            ============================================= */}

                            <div className="payment-out-form-field payment-out-form-field-wide">
                                <label>
                                    Supplier

                                    <span>
                                        *
                                    </span>
                                </label>

                                <select
                                    value={
                                        supplierId
                                    }
                                    disabled={
                                        supplierLoading ||
                                        loading.create ||
                                        Boolean(
                                            preselectedSupplier
                                        )
                                    }
                                    onChange={(event) =>
                                        handleSupplierChange(
                                            event
                                                .target
                                                .value
                                        )
                                    }
                                >
                                    <option value="">
                                        {supplierLoading
                                            ? "Loading suppliers..."
                                            : "Select supplier"}
                                    </option>

                                    {normalizedSuppliers.map(
                                        (
                                            supplier
                                        ) => {
                                            const currentSupplierId =
                                                getSupplierId(
                                                    supplier
                                                );

                                            const supplierName =
                                                getSupplierName(
                                                    supplier
                                                );

                                            const supplierMobile =
                                                getSupplierMobile(
                                                    supplier
                                                );

                                            const supplierGstin =
                                                getSupplierGstin(
                                                    supplier
                                                );

                                            return (
                                                <option
                                                    key={
                                                        currentSupplierId
                                                    }
                                                    value={
                                                        currentSupplierId
                                                    }
                                                >
                                                    {supplierName}

                                                    {supplierMobile !==
                                                        "-"
                                                        ? ` - ${supplierMobile}`
                                                        : ""}

                                                    {supplierGstin !==
                                                        "-"
                                                        ? ` - ${supplierGstin}`
                                                        : ""}
                                                </option>
                                            );
                                        }
                                    )}
                                </select>

                                {!supplierLoading &&
                                    !supplierError &&
                                    normalizedSuppliers.length ===
                                    0 && (
                                        <small className="payment-out-form-help">
                                            No active suppliers found. Add or activate a supplier from Supplier Master.
                                        </small>
                                    )}
                            </div>


                            {/* =============================================
                                PAYMENT DATE
                            ============================================= */}

                            <div className="payment-out-form-field">
                                <label>
                                    Payment Date
                                </label>

                                <input
                                    type="date"
                                    value={
                                        paymentDate
                                    }
                                    disabled={
                                        loading.create
                                    }
                                    onChange={(event) =>
                                        setPaymentDate(
                                            event
                                                .target
                                                .value
                                        )
                                    }
                                />
                            </div>


                            {/* =============================================
                                PAYMENT AMOUNT
                            ============================================= */}

                            <div className="payment-out-form-field">
                                <label>
                                    Payment Amount

                                    <span>
                                        *
                                    </span>
                                </label>

                                <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={
                                        amount
                                    }
                                    disabled={
                                        loading.create
                                    }
                                    placeholder="0.00"
                                    onChange={(event) => {
                                        setAmount(
                                            event
                                                .target
                                                .value
                                        );

                                        setLocalError(
                                            ""
                                        );
                                    }}
                                />
                            </div>


                            {/* =============================================
                                PAYMENT METHOD
                            ============================================= */}

                            <div className="payment-out-form-field">
                                <label>
                                    Payment Method
                                </label>

                                <select
                                    value={
                                        paymentMethod
                                    }
                                    disabled={
                                        loading.create
                                    }
                                    onChange={(event) => {
                                        const nextMethod =
                                            event
                                                .target
                                                .value;

                                        setPaymentMethod(
                                            nextMethod
                                        );

                                        /*
                                        |--------------------------------------------------------------------------
                                        | CLEAN CHEQUE DATA WHEN SWITCHING
                                        |--------------------------------------------------------------------------
                                        */

                                        if (
                                            nextMethod !==
                                            "CHEQUE"
                                        ) {
                                            setChequeNumber(
                                                ""
                                            );

                                            setChequeDate(
                                                ""
                                            );
                                        }

                                        if (
                                            ![
                                                "BANK_TRANSFER",
                                                "CHEQUE",
                                            ].includes(
                                                nextMethod
                                            )
                                        ) {
                                            setBankName(
                                                ""
                                            );
                                        }
                                    }}
                                >
                                    {PAYMENT_METHODS.map(
                                        (
                                            method
                                        ) => (
                                            <option
                                                key={
                                                    method.value
                                                }
                                                value={
                                                    method.value
                                                }
                                            >
                                                {
                                                    method.label
                                                }
                                            </option>
                                        )
                                    )}
                                </select>
                            </div>


                            {/* =============================================
                                REFERENCE
                            ============================================= */}

                            <div className="payment-out-form-field">
                                <label>
                                    Reference / UTR
                                </label>

                                <input
                                    type="text"
                                    value={
                                        referenceNumber
                                    }
                                    disabled={
                                        loading.create
                                    }
                                    placeholder="Transaction reference"
                                    onChange={(event) =>
                                        setReferenceNumber(
                                            event
                                                .target
                                                .value
                                        )
                                    }
                                />
                            </div>


                            {/* =============================================
                                BANK
                            ============================================= */}

                            {[
                                "BANK_TRANSFER",
                                "CHEQUE",
                            ].includes(
                                paymentMethod
                            ) && (
                                    <div className="payment-out-form-field">
                                        <label>
                                            Bank Name
                                        </label>

                                        <input
                                            type="text"
                                            value={
                                                bankName
                                            }
                                            disabled={
                                                loading.create
                                            }
                                            placeholder="Bank name"
                                            onChange={(event) =>
                                                setBankName(
                                                    event
                                                        .target
                                                        .value
                                                )
                                            }
                                        />
                                    </div>
                                )}


                            {/* =============================================
                                CHEQUE
                            ============================================= */}

                            {paymentMethod ===
                                "CHEQUE" && (
                                    <>
                                        <div className="payment-out-form-field">
                                            <label>
                                                Cheque Number

                                                <span>
                                                    *
                                                </span>
                                            </label>

                                            <input
                                                type="text"
                                                value={
                                                    chequeNumber
                                                }
                                                disabled={
                                                    loading.create
                                                }
                                                placeholder="Cheque number"
                                                onChange={(event) =>
                                                    setChequeNumber(
                                                        event
                                                            .target
                                                            .value
                                                    )
                                                }
                                            />
                                        </div>


                                        <div className="payment-out-form-field">
                                            <label>
                                                Cheque Date

                                                <span>
                                                    *
                                                </span>
                                            </label>

                                            <input
                                                type="date"
                                                value={
                                                    chequeDate
                                                }
                                                disabled={
                                                    loading.create
                                                }
                                                onChange={(event) =>
                                                    setChequeDate(
                                                        event
                                                            .target
                                                            .value
                                                    )
                                                }
                                            />
                                        </div>
                                    </>
                                )}
                        </div>
                    </section>


                    {/* =================================================
                        SELECTED SUPPLIER INFO
                    ================================================= */}

                    {selectedSupplier && (
                        <section className="payment-out-supplier-banner">

                            <div>
                                <span>
                                    Supplier
                                </span>

                                <strong>
                                    {getSupplierName(
                                        selectedSupplier
                                    )}
                                </strong>

                                <small>
                                    {getSupplierMobile(
                                        selectedSupplier
                                    )}
                                </small>
                            </div>


                            <div>
                                <span>
                                    GSTIN
                                </span>

                                <strong>
                                    {getSupplierGstin(
                                        selectedSupplier
                                    )}
                                </strong>

                                <small>
                                    Tax registration
                                </small>
                            </div>


                            <div>
                                <span>
                                    Outstanding Bills
                                </span>

                                <strong>
                                    {toNumber(
                                        outstandingSummary
                                            ?.totalBills
                                    )}
                                </strong>

                                <small>
                                    Pending purchase bills
                                </small>
                            </div>


                            <div>
                                <span>
                                    Total Outstanding
                                </span>

                                <strong>
                                    {formatCurrency(
                                        outstandingSummary
                                            ?.totalOutstanding
                                    )}
                                </strong>

                                <small>
                                    Before this payment
                                </small>
                            </div>

                        </section>
                    )}


                    {/* =================================================
                        OUTSTANDING PURCHASE BILLS
                    ================================================= */}

                    {supplierId && (
                        <section className="payment-out-allocation-card">

                            {/* HEADER */}

                            <div className="payment-out-allocation-header">

                                <div>
                                    <h3>
                                        Allocate Against Purchase Bills
                                    </h3>

                                    <p>
                                        Enter the amount to apply against each outstanding bill.
                                    </p>
                                </div>


                                <div className="payment-out-allocation-actions">

                                    <button
                                        type="button"
                                        disabled={
                                            loading.create ||
                                            loading.outstandingBills ||
                                            outstandingBills.length ===
                                            0
                                        }
                                        onClick={
                                            handleAllocateAll
                                        }
                                    >
                                        Allocate All
                                    </button>


                                    <button
                                        type="button"
                                        disabled={
                                            loading.create
                                        }
                                        onClick={
                                            handleClearAllocation
                                        }
                                    >
                                        Clear
                                    </button>

                                </div>
                            </div>


                            {/* SEARCH */}

                            <div className="payment-out-bill-search">
                                <FiSearch />

                                <input
                                    type="text"
                                    value={
                                        billSearch
                                    }
                                    disabled={
                                        loading.create
                                    }
                                    placeholder="Search outstanding purchase bill..."
                                    onChange={(event) =>
                                        setBillSearch(
                                            event
                                                .target
                                                .value
                                        )
                                    }
                                />
                            </div>


                            {/* LOADING */}

                            {loading.outstandingBills ? (
                                <div className="payment-out-dialog-loading">
                                    <FiLoader className="payment-out-spinner" />

                                    Loading outstanding bills...
                                </div>
                            ) : outstandingBills.length ===
                                0 ? (

                                /* =============================================
                                    EMPTY
                                ============================================= */

                                <div className="payment-out-dialog-empty">
                                    <FiCheckCircle />

                                    <strong>
                                        No outstanding purchase bills
                                    </strong>

                                    <span>
                                        This supplier currently has no unpaid purchase bills.
                                    </span>
                                </div>

                            ) : (

                                /* =============================================
                                    TABLE
                                ============================================= */

                                <div className="payment-out-allocation-table-wrapper">

                                    <table className="payment-out-allocation-table">

                                        <thead>
                                            <tr>
                                                <th>
                                                    Purchase Bill
                                                </th>

                                                <th>
                                                    Date
                                                </th>

                                                <th className="text-right">
                                                    Bill Amount
                                                </th>

                                                <th className="text-right">
                                                    Outstanding
                                                </th>

                                                <th className="text-right">
                                                    Allocate
                                                </th>

                                                <th>
                                                    Action
                                                </th>
                                            </tr>
                                        </thead>


                                        <tbody>
                                            {outstandingBills.map(
                                                (
                                                    bill,
                                                    index
                                                ) => {
                                                    const billId =
                                                        getBillId(
                                                            bill
                                                        );

                                                    const allocation =
                                                        allocations[
                                                        billId
                                                        ] ??
                                                        "";

                                                    return (
                                                        <tr
                                                            key={
                                                                billId ||
                                                                index
                                                            }
                                                        >
                                                            {/* BILL */}

                                                            <td>
                                                                <strong className="payment-out-bill-number">
                                                                    {getBillNumber(
                                                                        bill
                                                                    )}
                                                                </strong>
                                                            </td>


                                                            {/* DATE */}

                                                            <td>
                                                                {formatDate(
                                                                    getBillDate(
                                                                        bill
                                                                    )
                                                                )}
                                                            </td>


                                                            {/* BILL TOTAL */}

                                                            <td className="text-right">
                                                                {formatCurrency(
                                                                    getBillTotal(
                                                                        bill
                                                                    )
                                                                )}
                                                            </td>


                                                            {/* OUTSTANDING */}

                                                            <td className="text-right">
                                                                <strong className="payment-out-outstanding">
                                                                    {formatCurrency(
                                                                        getBillBalance(
                                                                            bill
                                                                        )
                                                                    )}
                                                                </strong>
                                                            </td>


                                                            {/* ALLOCATION */}

                                                            <td className="text-right">
                                                                <input
                                                                    type="number"
                                                                    min="0"
                                                                    max={
                                                                        getBillBalance(
                                                                            bill
                                                                        )
                                                                    }
                                                                    step="0.01"
                                                                    disabled={
                                                                        loading.create
                                                                    }
                                                                    value={
                                                                        allocation
                                                                    }
                                                                    placeholder="0.00"
                                                                    onChange={(event) =>
                                                                        handleAllocationChange(
                                                                            bill,
                                                                            event
                                                                                .target
                                                                                .value
                                                                        )
                                                                    }
                                                                />
                                                            </td>


                                                            {/* PAY FULL */}

                                                            <td>
                                                                <button
                                                                    type="button"
                                                                    className="payment-out-pay-full-button"
                                                                    disabled={
                                                                        loading.create
                                                                    }
                                                                    onClick={() =>
                                                                        handlePayFullBill(
                                                                            bill
                                                                        )
                                                                    }
                                                                >
                                                                    Pay Full
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    );
                                                }
                                            )}
                                        </tbody>

                                    </table>
                                </div>
                            )}
                        </section>
                    )}


                    {/* =================================================
                        PAYMENT SUMMARY
                    ================================================= */}

                    <section className="payment-out-dialog-summary">

                        <div>
                            <span>
                                Payment Amount
                            </span>

                            <strong>
                                {formatCurrency(
                                    paymentAmount
                                )}
                            </strong>
                        </div>


                        <div>
                            <span>
                                Allocated
                            </span>

                            <strong>
                                {formatCurrency(
                                    totalAllocated
                                )}
                            </strong>
                        </div>


                        <div
                            className={
                                amountShortage >
                                    0
                                    ? "is-error"
                                    : ""
                            }
                        >
                            <span>
                                Supplier Advance
                            </span>

                            <strong>
                                {formatCurrency(
                                    unallocatedAmount
                                )}
                            </strong>
                        </div>

                    </section>


                    {/* =================================================
                        NARRATION
                    ================================================= */}

                    <section className="payment-out-notes">
                        <label>
                            Narration / Notes
                        </label>

                        <textarea
                            rows="3"
                            maxLength="1000"
                            value={
                                narration
                            }
                            disabled={
                                loading.create
                            }
                            placeholder="Optional payment notes"
                            onChange={(event) =>
                                setNarration(
                                    event
                                        .target
                                        .value
                                )
                            }
                        />
                    </section>

                </div>


                {/* =====================================================
                    FOOTER
                ===================================================== */}

                <footer className="payment-out-dialog-footer">

                    <button
                        type="button"
                        disabled={
                            loading.create
                        }
                        onClick={
                            handleClose
                        }
                    >
                        Cancel
                    </button>


                    <button
                        type="button"
                        className="payment-out-dialog-submit"
                        disabled={
                            loading.create ||
                            supplierLoading ||
                            !supplierId ||
                            paymentAmount <=
                            0 ||
                            amountShortage >
                            0
                        }
                        onClick={
                            handleSubmit
                        }
                    >
                        {loading.create ? (
                            <>
                                <FiLoader className="payment-out-spinner" />

                                Recording...
                            </>
                        ) : (
                            <>
                                <FiCreditCard />

                                Record Payment
                            </>
                        )}
                    </button>

                </footer>
            </div>
        </div>
    );
};


export default MakePaymentDialog;